import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon, { type IconName } from "../../../components/AppIcon";
import { useListPaymentsQuery } from "../../../store/api/paymentsApi";
import type { PaymentStatus } from "../../../types";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDateTime } from "../../../utils/formatDate";

interface PaymentSummaryItem {
	id: number;
	tenant: string;
	unit: string;
	amount: string;
	status: PaymentStatus;
	dueDate: string;
	paidDate?: string;
}

const PaymentSummary: React.FC = () => {
	const navigate = useNavigate();

	// Fetch recent payments
	const { data: paymentsData, isLoading } = useListPaymentsQuery({
		page: 1,
		page_size: 10,
		ordering: "-created_at",
	});

	// Transform API data to component format
	const paymentData: PaymentSummaryItem[] = useMemo(() => {
		try {
			if (!paymentsData?.results) return [];

			return paymentsData.results.slice(0, 5).map((payment) => ({
				id: parseInt(payment?.id?.slice(-6) || "0", 16) || 0, // Convert ID to number for key
				tenant: payment?.tenant_name || "Unknown Tenant",
				unit: payment?.property_name || "N/A",
				amount: new Intl.NumberFormat("en-NG", {
					style: "currency",
					currency: "NGN",
				}).format(Number(payment?.amount) || 0),
				status: (payment?.status as PaymentStatus) || "pending",
				dueDate: payment?.due_date || "",
				paidDate: payment?.paid_date,
			}));
		} catch (error) {
			console.error("Error processing payment data:", error);
			return [];
		}
	}, [paymentsData]);

	const getStatusBadge = (status: PaymentStatus | string): JSX.Element => {
		const statusConfig: Record<
			string,
			{
				bg: string;
				text: string;
				border: string;
				icon: IconName;
				label: string;
			}
		> = {
			paid: {
				bg: "bg-success/20",
				text: "text-success",
				border: "border border-success/30",
				icon: "CheckCircle",
				label: "Paid",
			},
			pending: {
				bg: "bg-warning/20",
				text: "text-warning",
				border: "border border-warning/30",
				icon: "Clock",
				label: "Pending",
			},
			overdue: {
				bg: "bg-error/20",
				text: "text-error",
				border: "border border-error/30",
				icon: "AlertCircle",
				label: "Overdue",
			},
			cancelled: {
				bg: "bg-error/20",
				text: "text-error",
				border: "border border-error/30",
				icon: "X",
				label: "Cancelled",
			},
			refunded: {
				bg: "bg-muted/30",
				text: "text-foreground",
				border: "border border-border",
				icon: "RotateCcw",
				label: "Refunded",
			},
		};

		// Fallback for unknown statuses
		const config = statusConfig[status] || {
			bg: "bg-muted/30",
			text: "text-foreground",
			border: "border border-border",
			icon: "HelpCircle" as IconName,
			label: status || "Unknown",
		};

		return (
			<span
				className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} ${config.border}`}
			>
				<Icon name={config.icon} size={12} />
				<span>{config.label}</span>
			</span>
		);
	};

	// Using centralized formatDateTime utility

	// Helper function to safely parse amount to number
	const parseAmount = (amount: any): number => {
		if (amount === null || amount === undefined) return 0;
		if (typeof amount === "number") {
			return isNaN(amount) ? 0 : amount;
		}
		if (typeof amount === "string") {
			const parsed = parseFloat(amount);
			return isNaN(parsed) ? 0 : parsed;
		}
		return 0;
	};

	const totalRevenue = useMemo(() => {
		if (!paymentsData?.results) return 0;
		return paymentsData.results
			.filter((p) => p.status === "paid")
			.reduce((sum, p) => sum + parseAmount(p.amount), 0);
	}, [paymentsData]);

	const pendingAmount = useMemo(() => {
		if (!paymentsData?.results) return 0;
		return paymentsData.results
			.filter((p) => p.status === "pending" || p.status === "overdue")
			.reduce((sum, p) => sum + parseAmount(p.amount), 0);
	}, [paymentsData]);

	return (
		<div className="bg-card border border-border rounded-lg p-6 card-shadow flex flex-col h-full">
			<div className="flex items-center justify-between mb-6 flex-shrink-0">
				<h3 className="text-lg font-semibold text-foreground">
					Payment Summary
				</h3>
				<button
					onClick={() => navigate("/payment-management")}
					className="text-sm text-primary hover:text-primary/80 smooth-transition"
				>
					View All
				</button>
			</div>

			{/* Summary Cards */}
			<div className="grid grid-cols-2 gap-4 mb-6 flex-shrink-0">
				<div className="bg-success/15 border border-success/30 rounded-lg p-3 sm:p-4 overflow-hidden">
					<div className="flex items-center space-x-2 mb-2 gap-2">
						<Icon
							name="TrendingUp"
							size={14}
							className="text-success flex-shrink-0 sm:w-4 sm:h-4"
						/>
						<span className="text-xs sm:text-sm font-semibold text-success truncate">
							Collected
						</span>
					</div>
					<p className="text-base sm:text-lg font-bold text-foreground truncate">
						{formatCurrency(totalRevenue, { showAbbreviation: true })}
					</p>
				</div>
				<div className="bg-warning/15 border border-warning/30 rounded-lg p-3 sm:p-4 overflow-hidden">
					<div className="flex items-center space-x-2 mb-2 gap-2">
						<Icon
							name="Clock"
							size={14}
							className="text-warning flex-shrink-0 sm:w-4 sm:h-4"
						/>
						<span className="text-xs sm:text-sm font-semibold text-warning truncate">
							Pending
						</span>
					</div>
					<p className="text-base sm:text-lg font-bold text-foreground truncate">
						{formatCurrency(pendingAmount, { showAbbreviation: true })}
					</p>
				</div>
			</div>

			{/* Recent Payments */}
			<div className="flex-1 min-h-0 overflow-hidden">
				<div className="h-full overflow-y-auto space-y-3">
					{isLoading ? (
						<div className="text-center py-8 text-muted-foreground">
							Loading payments...
						</div>
					) : paymentData.length === 0 ? (
						<div className="text-center py-8 text-muted-foreground">
							No payments found
						</div>
					) : (
						paymentData.map((payment) => (
							<div
								key={payment.id}
								className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 smooth-transition flex-shrink-0"
							>
								<div className="flex-1 min-w-0">
									<div className="flex items-center justify-between mb-1 gap-2">
										<h4 className="text-sm font-medium text-foreground truncate">
											{payment.tenant}
										</h4>
										<span className="text-sm font-semibold text-foreground flex-shrink-0">
											{payment.amount}
										</span>
									</div>
									<div className="flex items-center justify-between gap-2">
										<span className="text-xs text-muted-foreground truncate">
											{payment.unit} • Due {formatDateTime(payment.dueDate)}
										</span>
										<div className="flex-shrink-0">
											{getStatusBadge(payment.status)}
										</div>
									</div>
								</div>
							</div>
						))
					)}
				</div>
			</div>
		</div>
	);
};

export default PaymentSummary;
